import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from '../pages/JobDashboard/JobDetailNew.module.css'; 
import { API_BASE_URL } from '../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import showToast from "../components/ShowToast";
import Loader2 from '../components/loader2';
import ModernNavbarPureCSS from "../components/Navbar/ModernNavBarPureCSS";
import Swal from 'sweetalert2';

import {
    ArrowLeft,
    Building,
    FileText,
    Camera,
    Briefcase,
    ShieldCheck,
    Check,
    Upload,
    Plus,
    X,
    Search,
    MapPin,
    ChevronRight,
    ChevronDown,
    Users,
    Award,
    GraduationCap,
    Hammer
} from "lucide-react";

// --- COMPONENT: Wizard Progress Bar ---
const WizardProgressBar = ({ currentStep, onStepClick, isEditingMode }) => {
    const steps = [
        { id: 1, label: 'Identity' },
        { id: 2, label: 'Location' },
        { id: 3, label: 'Expertise' },
        { id: 4, label: 'Credentials' },
        { id: 5, label: 'Verification' }
    ];

    const progressPercentage = Math.min(100, Math.max(0, ((currentStep - 1) / (steps.length - 1)) * 100));
    const goldGradient = 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)';

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto 2rem auto', padding: '0 1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', marginBottom: '10px' }}>
                {/* Background Grey Line */}
                <div style={{
                    position: 'absolute', top: '50%', left: '0', right: '0', height: '3px',
                    backgroundColor: '#e2e8f0', zIndex: 0, transform: 'translateY(-50%)', borderRadius: '2px'
                }}></div>
                
                {/* Active Gold Progress Line */}
                <div style={{
                    position: 'absolute', top: '50%', left: '0', height: '3px',
                    width: `${progressPercentage}%`, 
                    background: goldGradient, 
                    zIndex: 0, transform: 'translateY(-50%)', transition: 'width 0.3s ease', borderRadius: '2px'
                }}></div>

                {steps.map((step) => {
                    const isCompleted = step.id < currentStep;
                    const isActive = step.id === currentStep;
                    const isClickable = isEditingMode || step.id < currentStep;

                    return (
                        <div 
                            key={step.id} 
                            onClick={() => isClickable ? onStepClick(step.id) : null}
                            style={{ 
                                position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', 
                                alignItems: 'center', cursor: isClickable ? 'pointer' : 'default',
                                width: '60px' 
                            }}
                        >
                            <div style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: isCompleted || isActive ? goldGradient : '#fff',
                                border: isCompleted || isActive ? 'none' : '3px solid #e2e8f0',
                                color: isCompleted || isActive ? '#fff' : '#94a3b8',
                                fontWeight: '700', fontSize: '1rem',
                                textShadow: isCompleted || isActive ? '0 1px 2px rgba(180, 83, 9, 0.3)' : 'none',
                                boxShadow: isActive ? '0 0 0 4px rgba(251, 191, 36, 0.3)' : '0 2px 5px rgba(0,0,0,0.05)', 
                                transition: 'all 0.3s ease'
                            }}>
                                {isCompleted ? <Check size={20} strokeWidth={3} /> : step.id}
                            </div>
                            <span style={{
                                marginTop: '8px', fontSize: '0.7rem', fontWeight: isActive ? '700' : '600',
                                color: isActive ? '#d97706' : '#64748b', letterSpacing: '0.02em', textTransform: 'uppercase',
                                textAlign: 'center'
                            }} className={styles.hideOnMobile}>
                                {step.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- MAIN WIZARD COMPONENT ---
const BusinessOnboardingWizard = () => {
    const navigate = useNavigate();
    const { businessId: businessIdFromParams } = useParams();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [username, setUsername] = useState('');
    const [businessUserId, setBusinessUserId] = useState(null);
    const [businessId, setBusinessId] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState({ isValid: false, plan: 'Free' });
    const [validationErrors, setValidationErrors] = useState({});

    // --- GLOBAL STATE ---
    const [formData, setFormData] = useState({
        // Identity
        businessName: '', website: '', shortBio: '', longBio: '', abn: '', acn: '',
        profileImage: null, profileImageBase64: null, coverImageBase64: null,

        // Location
        addresses: [],

        // Expertise
        businessType: '',
        scope: { residential: false, commercial: false, government: false },
        afterHours: false, weekends: false,
        selectedProfessions: [],
        selectedServices: [],
        selectedProducts: [],

        // Credentials & Verification
        licenseNumber: '', licenseExpiry: '', isFullyInsured: false,
        selectedCertifications: [],
        selectedQualifications: [],
        selectedMemberships: [],
        registrationNumber: '', yearsExperience: 1,

        // Upload Status
        uploadStatus: {
            license: false, insurance: false, abn: false, 
            certification: false, qualification: false, membership: false
        },

        expiryDates: {
            license: '', insurance: '', membership: '', certification: '' 
        }
    });

    const isEditing = !!(formData.businessName && formData.addresses.length > 0);

    const updateForm = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const updateExpiryDate = (key, value) => {
        setFormData(prev => ({
            ...prev,
            expiryDates: { ...prev.expiryDates, [key]: value }
        }));
    };

    const updateUploadStatus = (key, value) => {
        setFormData(prev => ({
            ...prev,
            uploadStatus: { ...prev.uploadStatus, [key]: value }
        }));
    };

    // --- INITIAL DATA LOAD ---
    useEffect(() => {
        const loadInitialData = async () => {
            const token = localStorage.getItem('token');
            if (!token) { navigate('/login'); return; }
            const decoded = jwtDecode(token);
            setBusinessUserId(decoded.nameid);
            setUsername(decoded.unique_name || "Business");
            const bizId = decoded.BusinessId;
            setBusinessId(bizId);

            setIsLoading(true);

            try {
                const [profileRes, locRes, subRes, docsRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/BusinessProfile/getBusinessProfile?businessId=${bizId}`),
                    axios.get(`${API_BASE_URL}/api/BusinessProfile/getBusinessLocations?businessId=${bizId}`),
                    axios.get(`${API_BASE_URL}/api/Subscription/hasValidSubscription?bizid=${bizId}`).catch(() => ({ data: { isValid: false } })),
                    axios.get(`${API_BASE_URL}/api/BusinessOnboarding/get-uploaded-documents?businessId=${bizId}`).catch(() => ({ data: [] }))
                ]);

                // Map Documents
                const serverDocs = docsRes.data || [];
                const findDoc = (cat) => serverDocs.find(d => d.documentCategory === cat);
                const formatDate = (dateStr) => dateStr ? new Date(dateStr).toISOString().split('T')[0] : '';

                const newUploadStatus = {
                    license: !!findDoc('License'),
                    insurance: !!findDoc('Insurance'),
                    abn: !!findDoc('ABN'),
                    certification: !!findDoc('Certification'),
                    qualification: !!findDoc('Qualification'),
                    membership: !!findDoc('Membership')
                };

                const newExpiryDates = {
                    license: formatDate(findDoc('License')?.documentExpiry),
                    insurance: formatDate(findDoc('Insurance')?.documentExpiry),
                    membership: formatDate(findDoc('Membership')?.documentExpiry),
                    certification: formatDate(findDoc('Certification')?.documentExpiry)
                };

                setSubscriptionStatus(subRes.data);

                const b = profileRes.data.businesses;
                if (b) {
                    setFormData(prev => ({
                        ...prev,
                        businessName: b.businessName || '', 
                        website: b.website || '', 
                        abn: b.abn || '', 
                        acn: b.acn || '',
                        shortBio: b.description || '', 
                        longBio: b.profileDescription || '',
                        profileImageBase64: b.profilePic || null,
                        coverImageBase64: b.coverImage || null,
                        businessType: b.businessType || '',
                        scope: {
                            residential: b.residential === 1,
                            commercial: b.commercial === 1,
                            government: b.government === 1,
                        },
                        afterHours: b.afterHours === 1, 
                        weekends: b.weekends === 1,
                        selectedProfessions: profileRes.data.professions || [],
                        selectedServices: profileRes.data.services || [],
                        selectedProducts: (profileRes.data.products || []).map(p => ({ id: p.productId, name: p.productName })),
                        
                        licenseNumber: b.licenseNumber || '',
                        licenseExpiry: b.formattedLicenseExpiry || '',
                        isFullyInsured: b.fullyInsured,
                        yearsExperience: b.yearsExperience || 1,
                        registrationNumber: b.registrationNumber || '',
                        
                        selectedCertifications: (profileRes.data.certifications || []).map(c => c.certificationId),
                        selectedQualifications: (profileRes.data.qualificationDtos || []).map(q => q.qualificationId),
                        selectedMemberships: (profileRes.data.memberships || []).map(m => m.membershipId),
                        
                        uploadStatus: newUploadStatus,
                        expiryDates: newExpiryDates,
                    }));
                }

                if (locRes.data) {
                    setFormData(prev => ({
                        ...prev,
                        addresses: locRes.data.map(l => ({
                            address: `${l.streetNumber} ${l.streetName}, ${l.suburb}, ${l.myState} ${l.postcode}`,
                            mobile: l.phone, landline: l.landline, isPrimary: l.isPrimary === 1
                        }))
                    }));
                }

                // Smart Step Logic
                if (!businessIdFromParams) {
                    const isStep1Complete = b.businessName && b.abn && b.profilePic && b.description;
                    if (!isStep1Complete) setStep(1);
                    else if ((!locRes.data || locRes.data.length === 0)) setStep(2);
                    else if ((!profileRes.data.professions || profileRes.data.professions.length === 0)) setStep(3);
                    else setStep(4);
                }

            } catch (err) {
                console.error("Error loading profile:", err);
            } finally {
                setIsLoading(false);
            }
        };
        
        loadInitialData();
    }, [businessIdFromParams, navigate]);

    // --- VALIDATION LOGIC ---
    const validateStep = (currentStep) => {
        const errors = {};
        let isValid = true;

        if (currentStep === 1) {
            if (!formData.abn) errors.abn = "ABN is required";
            if (!formData.shortBio) errors.shortBio = "Short tagline is required";
            if (formData.website && !formData.website.startsWith('http')) {
                errors.website = "Website must start with http:// or https://";
            }
        }

        if (currentStep === 2) {
            if (formData.addresses.length === 0) {
                showToast('error', "Please add at least one location.");
                isValid = false;
            }
        }

        if (currentStep === 3) {
            if (!formData.businessType) {
                showToast('error', "Please select your business type.");
                isValid = false;
            }
            if (formData.selectedProfessions.length === 0) {
                showToast('error', "Please select at least one profession.");
                isValid = false;
            }
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            isValid = false;
            showToast('error', "Please check the highlighted fields.");
        }

        return isValid;
    };

    // --- NAVIGATION HANDLERS ---
    const handleNext = async () => {
        if (!validateStep(step)) return;

        setIsLoading(true);
        try {
            if (step === 1) {
                await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/updateBusinessOnboarding02`, {
                    businessUserId,
                    website: formData.website,
                    acn: formData.acn,
                    abn: formData.abn,
                    profileImage: formData.profileImageBase64,
                    coverImage: formData.coverImageBase64,
                    description: formData.shortBio,
                    profileDescription: formData.longBio
                });
            } else if (step === 2) {
                await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveBusinessLocations`, {
                    businessId,
                    locations: formData.addresses.map(addr => ({
                        address: addr.address, mobile: addr.mobile, landline: addr.landline, isPrimary: !!addr.isPrimary
                    }))
                });
            } else if (step === 3) {
                // Save Professions, Scope & Services
                await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveBusinessOnboarding03`, {
                    businessUserId,
                    businesstype: formData.businessType,
                    residential: formData.scope.residential ? 1 : 0,
                    commercial: formData.scope.commercial ? 1 : 0,
                    government: formData.scope.government ? 1 : 0,
                    afterHours: formData.afterHours ? 1 : 0,
                    weekends: formData.weekends ? 1 : 0,
                    professionIds: formData.selectedProfessions.map(p => p.professionId || p.id).filter(Boolean),
                    ancillaryIds: []
                });
                await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/savebusinessservices`, {
                    businessUserId,
                    serviceIds: formData.selectedServices.map(s => s.serviceId || s.id).filter(Boolean)
                });
                await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/savebusinessproducts`, {
                    businessUserId,
                    productIds: formData.selectedProducts.map(p => p.id).filter(Boolean)
                });
            } 
            
            setStep(prev => prev + 1);
            window.scrollTo(0, 0);
        } catch (error) {
            console.error("Save failed:", error);
            showToast('error', 'Failed to save progress. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFinalSubmit = async () => {
        const { license, insurance, abn } = formData.uploadStatus;
        let missing = [];
        if (!license) missing.push("License");
        if (!insurance) missing.push("Insurance");
        if (!abn) missing.push("ABN");

        if (missing.length > 0) {
            Swal.fire({ icon: 'error', title: 'Missing Documents', text: `Please upload: ${missing.join(', ')}` });
            return;
        }

        const today = new Date();
        today.setHours(0,0,0,0);
        let expired = [];

        const checkExpiry = (dateStr, name) => {
            if (dateStr) {
                const d = new Date(dateStr);
                if (d < today) expired.push(name);
            }
        };

        if (license) checkExpiry(formData.expiryDates.license, "License");
        if (insurance) checkExpiry(formData.expiryDates.insurance, "Insurance");
        if (formData.uploadStatus.membership) checkExpiry(formData.expiryDates.membership, "Membership");

        if (expired.length > 0) {
            Swal.fire({ 
                icon: 'error', title: 'Documents Expired', 
                html: `The following documents have expired:<br/><b>${expired.join(', ')}</b><br/><br/>Please update them before finishing.` 
            });
            return;
        }

        setIsLoading(true);

        try {
            // Save Accreditation (Including Certifications from Step 4)
            await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveaccreditation`, {
                businessUserId,
                licenseNumber: formData.licenseNumber,
                licenseExpiry: formData.licenseExpiry || null,
                fullyInsured: formData.isFullyInsured,
                certificationIds: formData.selectedCertifications,
            });

            // Save Qualifications & Memberships (From Step 4)
            await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveQualificationsAndMemberships`, {
                businessUserId,
                registrationNumber: formData.registrationNumber,
                yearsExperience: parseInt(formData.yearsExperience) || 1,
                qualificationIds: formData.selectedQualifications,
                membershipIds: formData.selectedMemberships, 
            });

            await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/updateOnboardingBusiness?businessUserId=${businessUserId}`);

            const isSubscribed = subscriptionStatus.isVerified;
            
            if (isSubscribed) {
                Swal.fire({
                    title: "🎉 Documents Submitted!",
                    html: `You have successfully onboarded.<br/><br/>Our team will review your verification documents. Your <b>Verified Pro</b> badge will appear once approved.`,
                    icon: "success",
                    confirmButtonText: "Go to Dashboard",
                    confirmButtonColor: '#d97706'
                }).then(() => {
                    localStorage.setItem('shouldOnboard', "0");
                    navigate('/businessdash');
                });
            } else {
                Swal.fire({
                    title: "Documents Received",
                    html: `<p style="color:#64748b">Your verification is now pending review.</p>...`,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "View Plans & Subscribe",
                    cancelButtonText: "Go to Dashboard",
                    confirmButtonColor: '#16a34a',
                    cancelButtonColor: '#64748b'
                }).then((result) => {
                    localStorage.setItem('shouldOnboard', "0");
                    if (result.isConfirmed) navigate('/subscription/subscribenew');
                    else navigate('/businessdash');
                });
            }
        } catch (error) {
            console.error("Final submit failed:", error);
            showToast('error', 'Failed to complete onboarding.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        setStep(prev => prev - 1);
        window.scrollTo(0, 0);
    };

    const handleCancel = () => {
        if (window.confirm("Exit onboarding? Your progress on previous steps has been saved.")) {
            navigate('/businessdash');
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1: return <Step1_Identity formData={formData} updateForm={updateForm} errors={validationErrors} businessUserId={businessUserId} />;
            case 2: return <Step2_Location formData={formData} updateForm={updateForm} />;
            case 3: return <Step3_Expertise formData={formData} updateForm={updateForm} />;
            case 4: return <Step4_Credentials formData={formData} updateForm={updateForm} />;
            case 5: return <Step5_Verification 
                             formData={formData} 
                             updateForm={updateForm} 
                             updateUploadStatus={updateUploadStatus} 
                             updateExpiryDate={updateExpiryDate} 
                             businessUserId={businessUserId} 
                           />;
            default: return null;
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', paddingTop: '65px', paddingBottom: '3rem' }}>
            <ModernNavbarPureCSS UserName={username} />
            <div style={{
                background: 'linear-gradient(to right, #0f172a 0%, #1e3a8a 100%)', 
                padding: '2rem 1rem',
                borderBottom: '4px solid #d97706', 
                color: 'white',
                marginBottom: '2rem',
                textAlign: 'center',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
            }}>
                <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '800', color: '#fff' }}>
                    {businessIdFromParams ? "Edit Your Profile" : "Welcome to BuildStart"}
                </h1>
                <p style={{ margin: '0.5rem 0 0 0', fontWeight: '500', opacity: 0.90, color: '#e2e8f0' }}>
                    {businessIdFromParams ? "Update your details below" : "Let's set up your professional profile to start winning jobs"}
                </p>
            </div>

            <WizardProgressBar 
                currentStep={step} 
                onStepClick={(stepId) => setStep(stepId)} 
                isEditingMode={isEditing} 
            />

            <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`} style={{ marginTop: '0', maxWidth: '800px' }}>
                {isLoading ? (
                    <div style={{ padding: '4rem', display: 'flex', justifyContent: 'center' }}><Loader2 /></div>
                ) : (
                    <>
                        {renderStep()}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', padding: '0 1rem' }}>
                            {step > 1 ? (
                                <button onClick={handleBack} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}>
                                    <ArrowLeft size={18} style={{ marginRight: '8px' }} /> Back
                                </button>
                            ) : (
                                <button onClick={handleCancel} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`}>
                                    Cancel
                                </button>
                            )}
                            {step < 5 ? (
                                <button onClick={handleNext} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`} style={{ minWidth: '120px' }}>
                                    Next Step <ChevronRight size={18} style={{ marginLeft: '8px' }} />
                                </button>
                            ) : (
                                <button onClick={handleFinalSubmit} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`} style={{ minWidth: '150px', backgroundColor: '#d97706' }}>
                                    Finish & Verify <ShieldCheck size={18} style={{ marginLeft: '8px' }} />
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ==========================================
// STEP 1: IDENTITY
// ==========================================
const Step1_Identity = ({ formData, updateForm, errors, businessUserId }) => {
    
    const saveImageImmediate = async (base64, isCover) => {
        try {
            await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/updateBusinessOnboarding02`, {
                businessUserId,
                [isCover ? 'coverImage' : 'profileImage']: base64,
                abn: formData.abn,
                businessName: formData.businessName
            });
            showToast('success', isCover ? 'Cover photo saved!' : 'Logo saved!');
        } catch (error) {
            console.error("Image auto-save failed", error);
        }
    };

    const handleImageUpload = (e) => {
        if (e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result;
                updateForm('profileImageBase64', result);
                saveImageImmediate(result, false); 
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCoverUpload = (e) => {
        if (e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result;
                updateForm('coverImageBase64', result);
                saveImageImmediate(result, true); 
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><Building className={styles.jobDetailNew_iconMd} /> Business Identity</h3>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                
                {/* Logo Upload */}
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                    <div style={{
                        width: '120px', height: '120px', borderRadius: '50%', border: '4px solid white',
                        margin: '0 auto 1rem auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', backgroundColor: '#fff', position: 'relative',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}>
                        {formData.profileImageBase64 ? (
                            <img src={formData.profileImageBase64} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <Camera size={40} color="#94a3b8" />
                        )}
                        <input type="file" accept="image/*" onChange={handleImageUpload}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                        />
                    </div>
                    <p className={styles.jobDetailNew_textSm} style={{ color: '#64748b' }}>Upload Business Logo</p>
                </div>

                <div>
                    <label className={styles.jobDetailNew_label}>Business Name</label>
                    <input type="text" value={formData.businessName} disabled className={styles.jobDetailNew_input} style={{ backgroundColor: '#f1f5f9' }} />
                </div>

                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`}>
                    <div>
                        <label className={styles.jobDetailNew_label}>Website (Optional)</label>
                        <input type="text" placeholder="https://" value={formData.website} onChange={e => updateForm('website', e.target.value)} 
                               className={`${styles.jobDetailNew_input} ${errors.website ? styles.jobDetailNew_inputError : ''}`} />
                        {errors.website && <p className={styles.jobDetailNew_textError} style={{color: 'red', fontSize: '0.8rem'}}>{errors.website}</p>}
                    </div>
                    <div>
                        <label className={styles.jobDetailNew_label}>ABN</label>
                        <input type="text" placeholder="Australian Business Number" value={formData.abn} onChange={e => updateForm('abn', e.target.value)} 
                               className={`${styles.jobDetailNew_input} ${errors.abn ? styles.jobDetailNew_inputError : ''}`} />
                        {errors.abn && <p className={styles.jobDetailNew_textError} style={{color: 'red', fontSize: '0.8rem'}}>{errors.abn}</p>}
                    </div>
                </div>

                <div>
                    <label className={styles.jobDetailNew_label}>Short Tagline <span style={{color:'red'}}>*</span></label>
                    <input type="text" placeholder="e.g. Sydney's Premier Electrical Team" value={formData.shortBio} onChange={e => updateForm('shortBio', e.target.value)} 
                           className={`${styles.jobDetailNew_input} ${errors.shortBio ? styles.jobDetailNew_inputError : ''}`} maxLength={100} />
                    {errors.shortBio && <p className={styles.jobDetailNew_textError} style={{color: 'red', fontSize: '0.8rem'}}>{errors.shortBio}</p>}
                </div>

                <div>
                    <label className={styles.jobDetailNew_label}>About Your Business</label>
                    <textarea placeholder="Tell clients about your experience, your team, and what makes you unique..." value={formData.longBio} onChange={e => updateForm('longBio', e.target.value)} className={styles.jobDetailNew_textarea} rows={5}></textarea>
                </div>

                {/* Cover Photo Upload */}
                <div style={{ marginBottom: '2rem' }}>
                    <label className={styles.jobDetailNew_label}>Cover Photo (Optional)</label>
                    <div style={{
                        width: '100%', height: '200px', borderRadius: '8px', border: '2px dashed #cbd5e1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', backgroundColor: '#f8fafc', position: 'relative',
                        backgroundImage: formData.coverImageBase64 ? `url(${formData.coverImageBase64})` : 'none',
                        backgroundSize: 'cover', backgroundPosition: 'center'
                    }}>
                        {!formData.coverImageBase64 && (
                            <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                                <Camera size={32} />
                                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Upload a wide landscape photo (1200x400px)</p>
                            </div>
                        )}
                        <input type="file" accept="image/*" onChange={handleCoverUpload}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// STEP 2: LOCATION
// ==========================================
const Step2_Location = ({ formData, updateForm }) => {
    const [searchInput, setSearchInput] = useState('');
    const [mobile, setMobile] = useState('');
    const [landline, setLandline] = useState('');
    const [results, setResults] = useState([]);

    const handleSearch = async (val) => {
        setSearchInput(val);
        if (val.length > 2) {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/GooglePlaces/addresscomplete`, { params: { input: val } });
                setResults(res.data?.predictions || []);
            } catch (e) { console.error(e); }
        } else {
            setResults([]);
        }
    };

    const addAddress = () => {
        if (!searchInput) return;
        const newAddr = {
            address: searchInput,
            mobile: mobile,
            landline: landline,
            isPrimary: formData.addresses.length === 0
        };
        updateForm('addresses', [...formData.addresses, newAddr]);
        setSearchInput(''); setMobile(''); setLandline(''); setResults([]);
    };

    const removeAddress = (index) => {
        const newArr = formData.addresses.filter((_, i) => i !== index);
        if (newArr.length > 0 && formData.addresses[index].isPrimary) {
            newArr[0].isPrimary = true;
        }
        updateForm('addresses', newArr);
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><MapPin className={styles.jobDetailNew_iconMd} /> Service Locations</h3>
            </div>
            <div className={styles.jobDetailNew_cardContent}>
                <p className={styles.jobDetailNew_textGray600} style={{ marginBottom: '1.5rem' }}>
                    Where are you based? You can add multiple office locations or depots.
                </p>

                {formData.addresses.map((addr, idx) => (
                    <div key={idx} className={styles.jobDetailNew_addressListItem} style={{ marginBottom: '1rem' }}>
                        <div className={styles.jobDetailNew_addressDetails}>
                            <MapPin size={20} className={styles.jobDetailNew_textBlue500} />
                            <div>
                                <p style={{ fontWeight: 600 }}>{addr.address} {addr.isPrimary && <span className={styles.jobDetailNew_badge}>Primary</span>}</p>
                                <p className={styles.jobDetailNew_textSm} style={{ color: '#64748b' }}>{addr.mobile} {addr.landline ? `/ ${addr.landline}` : ''}</p>
                            </div>
                        </div>
                        <button onClick={() => removeAddress(idx)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`}>
                            <X size={16} />
                        </button>
                    </div>
                ))}

                <div style={{ backgroundColor: '#f8fafc', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '1rem' }}>
                    <label className={styles.jobDetailNew_label}>Add a Location</label>
                    <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{ marginBottom: '1rem' }}>
                        <input type="tel" placeholder="Mobile Number" value={mobile} onChange={e => setMobile(e.target.value)} className={styles.jobDetailNew_input} />
                        <input type="tel" placeholder="Landline (Optional)" value={landline} onChange={e => setLandline(e.target.value)} className={styles.jobDetailNew_input} />
                    </div>
                    <div style={{ position: 'relative' }}>
                        <input type="text" placeholder="Search Address..." value={searchInput} onChange={e => handleSearch(e.target.value)} className={styles.jobDetailNew_input} />
                        {results.length > 0 && (
                            <ul className={styles.jobDetailNew_dropdownResults}>
                                {results.map(r => (
                                    <li key={r.place_id} className={styles.jobDetailNew_resultItem} onClick={() => { setSearchInput(r.description); setResults([]); }}>
                                        {r.description}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <button onClick={addAddress} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`} style={{ marginTop: '1rem', width: '100%' }}>
                        <Plus size={16} style={{ marginRight: '8px' }} /> Add Location
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// STEP 3: EXPERTISE (With Auto-Remove Services)
// ==========================================
const Step3_Expertise = ({ formData, updateForm }) => {
    const [profSearch, setProfSearch] = useState('');
    const [profResults, setProfResults] = useState([]);

    const handleProfSearch = async (val) => {
        setProfSearch(val);
        if (val.length > 1) {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/searchprofessions?search=${val}`);
                setProfResults(res.data);
            } catch (e) { console.error(e); }
        } else {
            setProfResults([]);
        }
    };

    // Feature 1: Auto-attach services on profession selection
    const selectProfession = async (item) => {
        if (!formData.selectedProfessions.some(p => p.id === item.id)) {
            const newProfessions = [...formData.selectedProfessions, item];
            updateForm('selectedProfessions', newProfessions);

            try {
                // Fetch Services automatically
                const response = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/servicesByProfession/${item.id}`);
                const services = response.data;
                
                if (services && services.length > 0) {
                    const currentServiceIds = new Set(formData.selectedServices.map(s => s.id || s.serviceId));
                    const newServices = services.filter(s => !currentServiceIds.has(s.id));
                    
                    if (newServices.length > 0) {
                        updateForm('selectedServices', [...formData.selectedServices, ...newServices]);
                        showToast('success', `Added ${newServices.length} services for ${item.name}`);
                    }
                }
            } catch (e) { console.error("Error auto-adding services", e); }
        }
        setProfSearch(''); setProfResults([]);
    };

    // Feature 2: Auto-delete services on profession removal
    const handleRemoveProfession = async (profId) => {
        // 1. Visually remove profession immediately for UI responsiveness
        const updatedProfessions = formData.selectedProfessions.filter(p => p.id !== profId);
        updateForm('selectedProfessions', updatedProfessions);

        try {
            // 2. Fetch services associated with this profession to identify what to delete
            // This ensures we clean up correctly even if data was loaded from backend without local tags
            const response = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/servicesByProfession/${profId}`);
            const servicesToRemove = response.data || [];
            
            if (servicesToRemove.length > 0) {
                const idsToRemove = new Set(servicesToRemove.map(s => s.id));
                
                // 3. Filter out services that match the IDs
                const remainingServices = formData.selectedServices.filter(s => 
                    !idsToRemove.has(s.id || s.serviceId)
                );
                
                const countRemoved = formData.selectedServices.length - remainingServices.length;
                if (countRemoved > 0) {
                    updateForm('selectedServices', remainingServices);
                    showToast('info', `Removed services associated with profession.`);
                }
            }
        } catch (e) {
            console.error("Could not auto-remove services", e);
        }
    };

    const removeService = (serviceId) => {
        updateForm('selectedServices', formData.selectedServices.filter(s => (s.id || s.serviceId) !== serviceId));
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><Briefcase className={styles.jobDetailNew_iconMd} /> Expertise</h3>
            </div>
            <div className={styles.jobDetailNew_cardContent}>
                <div style={{ marginBottom: '2rem' }}>
                    <label className={styles.jobDetailNew_label}>I am a...</label>
                    <div className={styles.jobDetailNew_timelineButtonGrid}>
                        {['Trade Professional', 'Building Professional', 'Supplier'].map(type => (
                            <button key={type} className={`${styles.jobDetailNew_button} ${formData.businessType === type ? styles.jobDetailNew_bgBlue500 : styles.jobDetailNew_buttonOutline}`}
                                onClick={() => updateForm('businessType', type)}>
                                {type}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ marginBottom: '2rem' }}>
                    <label className={styles.jobDetailNew_label}>My Trades / Professions</label>
                    <div style={{ position: 'relative', marginBottom: '1rem' }}>
                        <Search className={styles.jobDetailNew_inputIcon} />
                        <input className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} placeholder="Search (e.g. Plumber, Architect)..."
                            value={profSearch} onChange={e => handleProfSearch(e.target.value)} />
                        {profResults.length > 0 && (
                            <ul className={styles.jobDetailNew_dropdownResults}>
                                {profResults.map(p => (
                                    <li key={p.id} className={styles.jobDetailNew_resultItem} onClick={() => selectProfession(p)}>{p.name}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className={styles.jobDetailNew_flexWrap}>
                        {formData.selectedProfessions.map(p => (
                            <span key={p.id} className={styles.jobDetailNew_badge}>
                                {p.name} <button onClick={() => handleRemoveProfession(p.id)} style={{ marginLeft: '5px', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>×</button>
                            </span>
                        ))}
                    </div>
                </div>

                {/* Services Display - Auto Added */}
                {formData.selectedServices.length > 0 && (
                    <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px' }}>
                         <label className={styles.jobDetailNew_label} style={{marginBottom: '0.5rem', display: 'flex', alignItems: 'center'}}>
                            <Hammer size={16} style={{marginRight: '6px'}}/> Services Provided
                            <span style={{fontSize: '0.75rem', color: '#64748b', fontWeight: 400, marginLeft: 'auto'}}>Auto-selected based on professions</span>
                        </label>
                        <div className={styles.jobDetailNew_flexWrap}>
                            {formData.selectedServices.map(s => (
                                <span key={s.id || s.serviceId} className={styles.jobDetailNew_badge} style={{backgroundColor: '#e0f2fe', color: '#0369a1'}}>
                                    {s.name} 
                                    <button onClick={() => removeService(s.id || s.serviceId)} style={{ marginLeft: '5px', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>×</button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <label className={styles.jobDetailNew_label}>Work Scope</label>
                    <div className={styles.jobDetailNew_timelineButtonGrid}>
                        {['residential', 'commercial', 'government'].map(s => (
                            <button key={s} className={`${styles.jobDetailNew_button} ${formData.scope[s] ? styles.jobDetailNew_bgGreen500 : styles.jobDetailNew_buttonOutline}`}
                                onClick={() => updateForm('scope', { ...formData.scope, [s]: !formData.scope[s] })}>
                                {s.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// STEP 4: CREDENTIALS
// ==========================================
const Step4_Credentials = ({ formData, updateForm }) => {
    const [availableCertifications, setAvailableCertifications] = useState([]);
    const [availableQualifications, setAvailableQualifications] = useState([]);
    const [availableMemberships, setAvailableMemberships] = useState([]);
    const [loading, setLoading] = useState(false);

    // Feature 3: Fetch relevant certs/quals based on professions
    useEffect(() => {
        const fetchData = async () => {
            if (formData.selectedProfessions.length === 0) {
                setAvailableCertifications([]); 
                setAvailableQualifications([]);
                setAvailableMemberships([]);
                return;
            }

            setLoading(true);
            const professionIds = formData.selectedProfessions.map(p => p.id || p.professionId);
            const params = new URLSearchParams();
            professionIds.forEach(id => params.append('professionIds', id));
            const queryString = params.toString();

            try {
                const [certs, quals, memberships] = await Promise.all([
                  axios.get(`${API_BASE_URL}/api/BusinessOnboarding/certifications-by-professions?${queryString}`),
                  axios.get(`${API_BASE_URL}/api/BusinessOnboarding/qualifications-by-professions?${queryString}`),
                  axios.get(`${API_BASE_URL}/api/BusinessOnboarding/memberships-by-professions?${queryString}`)
                ]);
                setAvailableCertifications(certs.data);
                setAvailableQualifications(quals.data);
                setAvailableMemberships(memberships.data);
            } catch (err) {
                console.error("Failed to fetch credentials", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [formData.selectedProfessions]);

    const toggleSelection = (key, id) => {
        const current = formData[key] || [];
        const updated = current.includes(id) 
            ? current.filter(item => item !== id) 
            : [...current, id];
        updateForm(key, updated);
    };

    return (
        <div className={styles.jobDetailNew_card}>
            <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}><GraduationCap className={styles.jobDetailNew_iconMd} /> Qualifications & Accreditation</h3>
            </div>
            <div className={styles.jobDetailNew_cardContent}>
                
                {formData.selectedProfessions.length === 0 ? (
                    <p style={{color: '#64748b', fontStyle: 'italic'}}>Please select a profession in the previous step to see relevant qualifications.</p>
                ) : loading ? (
                    <div style={{padding: '2rem', textAlign: 'center'}}><Loader2 /></div>
                ) : (
                    <>
                        <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{ marginBottom: '2rem' }}>
                            <div>
                                <label className={styles.jobDetailNew_label}>Years of Experience</label>
                                <input type="number" min="1" value={formData.yearsExperience} onChange={e => updateForm('yearsExperience', e.target.value)} className={styles.jobDetailNew_input} />
                            </div>
                            <div>
                                <label className={styles.jobDetailNew_label}>Registration Number (Optional)</label>
                                <input placeholder="e.g. Builders Reg" value={formData.registrationNumber} onChange={e => updateForm('registrationNumber', e.target.value)} className={styles.jobDetailNew_input} />
                            </div>
                        </div>

                        {/* Certifications */}
                        <div style={{ marginBottom: '2rem' }}>
                            <label className={styles.jobDetailNew_label} style={{display:'flex', gap:'8px', alignItems:'center'}}>
                                <Award size={16}/> Relevant Certifications
                            </label>
                            {availableCertifications.length === 0 && <p style={{fontSize:'0.85rem', color:'#94a3b8'}}>No specific certifications found for your professions.</p>}
                            <div className={styles.jobDetailNew_flexWrap}>
                                {availableCertifications.map(c => (
                                    <button 
                                        key={c.certificationId} 
                                        onClick={() => toggleSelection('selectedCertifications', c.certificationId)}
                                        style={{
                                            padding: '8px 14px', borderRadius: '20px', border: '1px solid', cursor: 'pointer', fontSize: '0.9rem',
                                            background: formData.selectedCertifications.includes(c.certificationId) ? '#eff6ff' : 'white',
                                            color: formData.selectedCertifications.includes(c.certificationId) ? '#1e40af' : '#64748b',
                                            borderColor: formData.selectedCertifications.includes(c.certificationId) ? '#2563eb' : '#cbd5e1'
                                        }}
                                    >
                                        {c.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Qualifications */}
                        <div style={{ marginBottom: '2rem' }}>
                            <label className={styles.jobDetailNew_label} style={{display:'flex', gap:'8px', alignItems:'center'}}>
                                <GraduationCap size={16}/> Qualifications
                            </label>
                            {availableQualifications.length === 0 && <p style={{fontSize:'0.85rem', color:'#94a3b8'}}>No specific qualifications found.</p>}
                            <div className={styles.jobDetailNew_flexWrap}>
                                {availableQualifications.map(q => (
                                    <button 
                                        key={q.qualificationId} 
                                        onClick={() => toggleSelection('selectedQualifications', q.qualificationId)}
                                        style={{
                                            padding: '8px 14px', borderRadius: '20px', border: '1px solid', cursor: 'pointer', fontSize: '0.9rem',
                                            background: formData.selectedQualifications.includes(q.qualificationId) ? '#eff6ff' : 'white',
                                            color: formData.selectedQualifications.includes(q.qualificationId) ? '#1e40af' : '#64748b',
                                            borderColor: formData.selectedQualifications.includes(q.qualificationId) ? '#2563eb' : '#cbd5e1'
                                        }}
                                    >
                                        {q.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Memberships */}
                        <div>
                            <label className={styles.jobDetailNew_label} style={{display:'flex', gap:'8px', alignItems:'center'}}>
                                <Users size={16}/> Industry Memberships
                            </label>
                            {availableMemberships.length === 0 && <p style={{fontSize:'0.85rem', color:'#94a3b8'}}>No industry memberships found.</p>}
                            <div className={styles.jobDetailNew_flexWrap}>
                                {availableMemberships.map(m => (
                                    <button 
                                        key={m.membershipId} 
                                        onClick={() => toggleSelection('selectedMemberships', m.membershipId)}
                                        style={{
                                            padding: '8px 14px', borderRadius: '20px', border: '1px solid', cursor: 'pointer', fontSize: '0.9rem',
                                            background: formData.selectedMemberships.includes(m.membershipId) ? '#eff6ff' : 'white',
                                            color: formData.selectedMemberships.includes(m.membershipId) ? '#1e40af' : '#64748b',
                                            borderColor: formData.selectedMemberships.includes(m.membershipId) ? '#2563eb' : '#cbd5e1'
                                        }}
                                    >
                                        {m.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ==========================================
// STEP 5: VERIFICATION 
// ==========================================
const VerificationItem = ({ title, icon: Icon, isMandatory, isOpen, onClick, children, isUploaded }) => (
    <div style={{
        border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '1rem',
        backgroundColor: 'white', overflow: 'hidden'
    }}>
        <div 
            onClick={onClick}
            style={{
                padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                cursor: 'pointer', backgroundColor: isOpen ? '#fffbeb' : 'white',
                borderLeft: isUploaded ? '4px solid #10b981' : (isMandatory ? '4px solid #d97706' : '4px solid #cbd5e1')
            }}
        >
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                <div style={{
                    padding: '8px', borderRadius: '50%', 
                    backgroundColor: isUploaded ? '#d1fae5' : '#f1f5f9',
                    color: isUploaded ? '#059669' : '#64748b'
                }}>
                    {isUploaded ? <Check size={20}/> : <Icon size={20} />}
                </div>
                <div>
                    <h4 style={{margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#1e293b'}}>
                        {title} {isMandatory && <span style={{fontSize: '0.7rem', color: '#ef4444', backgroundColor: '#fee2e2', padding: '2px 6px', borderRadius: '4px', marginLeft: '6px'}}>REQUIRED</span>}
                    </h4>
                    <p style={{margin: 0, fontSize: '0.8rem', color: '#64748b'}}>
                        {isUploaded ? 'Document received' : 'Pending upload'}
                    </p>
                </div>
            </div>
            <div>
               {isOpen ? <ChevronDown size={18} color="#94a3b8"/> : <ChevronRight size={18} color="#94a3b8"/>}
            </div>
        </div>
        {isOpen && <div style={{padding: '1.5rem', borderTop: '1px solid #f1f5f9'}}>{children}</div>}
    </div>
);

const Step5_Verification = ({ formData, updateForm, updateUploadStatus, updateExpiryDate, businessUserId }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [openSection, setOpenSection] = useState('license'); 

    const handleFileUpload = async (file, categoryString, stateKey, requiresExpiry = false) => {
        if (!file) return;

        if (requiresExpiry) {
            const dateVal = formData.expiryDates[stateKey];
            if (!dateVal) {
                showToast('error', `Please enter the Expiry Date for your ${categoryString} before uploading.`);
                return;
            }
            const selectedDate = new Date(dateVal);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (selectedDate < today) {
                showToast('error', `This ${categoryString} has expired. Please upload a valid document.`);
                return;
            }
        }
        
        setIsUploading(true);
        const uploadData = new FormData();
        uploadData.append('BusinessUserId', businessUserId);
        uploadData.append('DocumentCategory', categoryString);
        uploadData.append('File', file);
        if (formData.expiryDates[stateKey]) {
            uploadData.append('DocumentExpiry', formData.expiryDates[stateKey]);
        }

        try {
            const response = await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/upload-verification-doc`, uploadData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (response.status === 200) {
                showToast('success', `${categoryString} uploaded successfully!`);
                updateUploadStatus(stateKey, true);
            }
        } catch (error) {
            console.error(error);
            showToast('error', 'Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={styles.jobDetailNew_card} style={{border: '2px solid #d97706', overflow: 'hidden'}}>
            <div style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
                padding: '1.5rem', color: 'white', textAlign: 'center',
                textShadow: '0 1px 2px rgba(180, 83, 9, 0.2)'
            }}>
                <ShieldCheck size={48} style={{ marginBottom: '10px' }} />
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Verification Center</h2>
                <p style={{ opacity: 0.9 }}>Upload documents to unlock your <strong>Verified Pro Badge</strong>.</p>
            </div>

            <div className={styles.jobDetailNew_cardContent} style={{backgroundColor: '#f8fafc'}}>
                
                <VerificationItem 
                    title="License Documentation" icon={FileText} isMandatory={true}
                    isUploaded={formData.uploadStatus.license} 
                    isOpen={openSection === 'license'}
                    onClick={() => setOpenSection(openSection === 'license' ? '' : 'license')}
                >
                    <label className={styles.jobDetailNew_label}>Regisration / License Number</label>
                    <input type="text" className={styles.jobDetailNew_input} placeholder="e.g. 123456C" value={formData.licenseNumber} onChange={e => updateForm('licenseNumber', e.target.value)} style={{marginBottom: '1rem'}} />
                    <label className={styles.jobDetailNew_label}>Expiry Date</label>
                    <input type="date" className={styles.jobDetailNew_input} value={formData.expiryDates.license} onChange={(e) => updateExpiryDate('license', e.target.value)} style={{marginBottom: '1rem'}} />
                    <label className={styles.jobDetailNew_label}>Upload License Card</label>
                    <DragDropUploadBox id="license-drop" isUploading={isUploading} onFileSelect={(file) => handleFileUpload(file, 'License', 'license', true)} />
                </VerificationItem>

                <VerificationItem 
                    title="Insurance Documents" icon={ShieldCheck} isMandatory={true}
                    isUploaded={formData.uploadStatus.insurance} 
                    isOpen={openSection === 'insurance'}
                    onClick={() => setOpenSection(openSection === 'insurance' ? '' : 'insurance')}
                >
                    <div className={styles.jobDetailNew_flexItemsCenter} style={{gap: '10px', marginBottom: '1rem'}}>
                         <button className={`${styles.jobDetailNew_switch} ${formData.isFullyInsured ? styles.jobDetailNew_switchActive : ''}`} onClick={() => updateForm('isFullyInsured', !formData.isFullyInsured)}><span className={styles.jobDetailNew_switchToggle}></span></button>
                         <span style={{fontWeight: 500}}>I have valid Public Liability Insurance</span>
                    </div>
                    <label className={styles.jobDetailNew_label}>Expiry Date</label>
                    <input type="date" className={styles.jobDetailNew_input} value={formData.expiryDates.insurance} onChange={(e) => updateExpiryDate('insurance', e.target.value)} style={{marginBottom: '1rem'}} />
                    <label className={styles.jobDetailNew_label}>Upload Certificate of Currency</label>
                    <DragDropUploadBox id="insurance-drop" isUploading={isUploading} onFileSelect={(file) => handleFileUpload(file, 'Insurance', 'insurance', true)} />
                </VerificationItem>

                <VerificationItem 
                    title="ABN / Business Registration" icon={Building} isMandatory={true}
                    isUploaded={formData.uploadStatus.abn} 
                    isOpen={openSection === 'abn'}
                    onClick={() => setOpenSection(openSection === 'abn' ? '' : 'abn')}
                >
                    <label className={styles.jobDetailNew_label}>Upload ASIC Record</label>
                    <DragDropUploadBox id="abn-drop" isUploading={isUploading} onFileSelect={(file) => handleFileUpload(file, 'ABN', 'abn', false)} />
                </VerificationItem>

                {/* Additional Optional Items */}
                <VerificationItem 
                    title="Certification Documentation" icon={Award} isMandatory={false}
                    isUploaded={formData.uploadStatus.certification} 
                    isOpen={openSection === 'certification'}
                    onClick={() => setOpenSection(openSection === 'certification' ? '' : 'certification')}
                >
                    <label className={styles.jobDetailNew_label}>Expiry Date (If applicable)</label>
                    <input type="date" className={styles.jobDetailNew_input} value={formData.expiryDates.certification} onChange={(e) => updateExpiryDate('certification', e.target.value)} style={{marginBottom: '1rem'}} />                    
                    <label className={styles.jobDetailNew_label}>Upload Certificate</label>
                    <DragDropUploadBox id="cert-drop" isUploading={isUploading} onFileSelect={(file) => handleFileUpload(file, 'Certification', 'certification', false)} />
                </VerificationItem>

                <VerificationItem 
                    title="Qualification Documents" icon={Award} isMandatory={false}
                    isUploaded={formData.uploadStatus.qualification} 
                    isOpen={openSection === 'qualification'}
                    onClick={() => setOpenSection(openSection === 'qualification' ? '' : 'qualification')}
                >
                    <label className={styles.jobDetailNew_label}>Upload Transcript</label>
                    <DragDropUploadBox id="qual-drop" isUploading={isUploading} onFileSelect={(file) => handleFileUpload(file, 'Qualification', 'qualification', false)} />
                </VerificationItem>

                <VerificationItem 
                    title="Industry Body Membership" icon={Users} isMandatory={false}
                    isUploaded={formData.uploadStatus.membership} 
                    isOpen={openSection === 'membership'}
                    onClick={() => setOpenSection(openSection === 'membership' ? '' : 'membership')}
                >
                     <label className={styles.jobDetailNew_label}>Renewal Date</label>
                    <input type="date" className={styles.jobDetailNew_input} value={formData.expiryDates.membership} onChange={(e) => updateExpiryDate('membership', e.target.value)} style={{marginBottom: '1rem'}} />
                    <label className={styles.jobDetailNew_label}>Upload Membership</label>
                    <DragDropUploadBox id="mem-drop" isUploading={isUploading} onFileSelect={(file) => handleFileUpload(file, 'Membership', 'membership', true)} />
                </VerificationItem>

            </div>
        </div>
    );
};

const DragDropUploadBox = ({ id, isUploading, onFileSelect, accept = ".pdf, .jpg, .jpeg, .png" }) => {
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);
    const dragCounter = useRef(0);

    const handleDrag = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (e.type === "dragenter") {
            dragCounter.current += 1;
            if (e.dataTransfer.items && e.dataTransfer.items.length > 0) setDragActive(true);
        } else if (e.type === "dragleave") {
            dragCounter.current -= 1;
            if (dragCounter.current === 0) setDragActive(false);
        } else if (e.type === "dragover") {
            if (!dragActive) setDragActive(true);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActive(false); dragCounter.current = 0;
        if (e.dataTransfer.files && e.dataTransfer.files[0]) onFileSelect(e.dataTransfer.files[0]);
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) onFileSelect(e.target.files[0]);
    };

    return (
        <div 
            onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            onClick={() => !isUploading && inputRef.current.click()} 
            style={{
                border: dragActive ? '2px solid #2563eb' : '2px dashed #cbd5e1',
                backgroundColor: dragActive ? '#eff6ff' : '#f8fafc',
                borderRadius: '8px', padding: '2rem', textAlign: 'center', transition: 'all 0.2s ease',
                cursor: isUploading ? 'default' : 'pointer', position: 'relative', overflow: 'hidden'
            }}
        >
            <input ref={inputRef} type="file" id={id} onChange={handleChange} accept={accept} style={{display: 'none'}} disabled={isUploading} />
            {isUploading ? ( <Loader2 /> ) : (
                <div style={{ pointerEvents: 'none' }}> 
                    <div style={{
                        width: '50px', height: '50px', borderRadius: '50%', 
                        backgroundColor: dragActive ? '#dbeafe' : '#e2e8f0', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto'
                    }}>
                        <Upload size={24} color={dragActive ? '#2563eb' : '#64748b'} />
                    </div>
                    <p style={{margin: '0 0 0.5rem 0', fontWeight: '600', color: '#1e293b'}}>{dragActive ? "Drop file here" : "Click or drag file to upload"}</p>
                    <p style={{margin: 0, fontSize: '0.8rem', color: '#94a3b8'}}>PDF, JPG, PNG (Max 10MB)</p>
                </div>
            )}
        </div>
    );
};

export default BusinessOnboardingWizard;